import TouchAppOutlined from "@mui/icons-material/TouchAppOutlined";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { AssistantLogo } from "./assistant-logo";

import type { AssistantInfo } from "./assistants";
import type { FC } from "react";

interface DisconnectDialogProps {
  assistant: AssistantInfo;
  isLast: boolean;
  onDisconnect: () => Promise<void>;
  onClose: () => void;
}

export const DisconnectDialog: FC<DisconnectDialogProps> = ({
  assistant,
  isLast,
  onDisconnect,
  onClose,
}) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  const disconnect = async (): Promise<void> => {
    setSaving(true);
    setError(false);
    try {
      await onDisconnect();
      onClose();
    } catch (disconnectError) {
      console.error("Couldn’t reset the setup", disconnectError);
      setError(true);
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth aria-labelledby="mcp-disconnect-title">
      <DialogTitle
        id="mcp-disconnect-title"
        sx={{ display: "flex", alignItems: "center", gap: 1.5, fontWeight: 700 }}
      >
        <AssistantLogo assistant={assistant} size={40} />
        Disconnect {assistant.name}?
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.75}>
          {error ? (
            <Alert severity="error">Couldn’t disconnect {assistant.name}. Please try again.</Alert>
          ) : null}
          <Alert severity="info" icon={<TouchAppOutlined />}>
            <strong>First, remove ChangeEngine in {assistant.name}:</strong> {assistant.removePath}.
            ChangeEngine can’t do this for you.
          </Alert>
          <Typography variant="body2">
            Disconnecting here resets your setup for {assistant.name} on this page. It doesn’t
            change anything in {assistant.name} or for your teammates.
          </Typography>
          {isLast ? (
            <Alert severity="warning">
              This is your last connected assistant, so you’ll see the setup guide on this page
              again.
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          onClick={onClose}
          disabled={saving}
          data-analytics-id="mcp-connect-disconnect-cancel"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          disabled={saving}
          onClick={() => void disconnect()}
          data-analytics-id="mcp-connect-disconnect-confirm"
        >
          Disconnect
        </Button>
      </DialogActions>
    </Dialog>
  );
};
